import React from 'react';
import {Box, styled} from '@mui/material';
import {media} from '@/styles/mediaQueries';

export interface PageProps {
  /**
   * The content to be rendered inside the page
   */
  children: React.ReactNode;

  /**
   * Unique identifier for the page
   */
  id?: string;

  /**
   * Additional CSS class name for custom styling
   */
  className?: string;

  /**
   * Whether the page should have a maximum width and be centered
   * Default: true
   */
  constrained?: boolean;

  /**
   * The maximum width of the page content
   * Default: '1200px'
   */
  maxWidth?: string;

  /**
   * The padding around the page content
   * Default: '32px'
   */
  padding?: string;
}

const StyledPageContainer = styled(Box)(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
}));

const shouldNotForwardProps = ['constrained'];

const StyledPageContent = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})<{
  constrained?: boolean;
  maxWidth?: string;
  padding?: string;
}>(({constrained = true, maxWidth = '1200px', padding = '50px'}) => ({
  flex: 1,
  padding,
  width: '100%',
  boxSizing: 'border-box',
  [media.mobile]: {
    marginTop: '70px',
    padding: '20px',
  },
  ...(constrained && {
    maxWidth,
    margin: '0 auto',
  }),
}));

/**
 * A generic page component that provides consistent layout for all pages
 */
function Page({
  children,
  id,
  className,
  constrained = true,
  maxWidth = '1200px',
  padding = '50px',
}: PageProps) {
  return (
    <StyledPageContainer id={id} className={className}>
      <StyledPageContent
        constrained={constrained}
        maxWidth={maxWidth}
        padding={padding}
      >
        {children}
      </StyledPageContent>
    </StyledPageContainer>
  );
}

export default Page;
